/**
 * Helpers de range de data — sem dependências externas (vanilla Date).
 *
 * Convenção pt-BR: semana começa na **segunda** (weekStartsOn: 1). Funções
 * retornam objetos `Date` na timezone local do browser; converter pra ISO
 * UTC usa `.toISOString()` no caller.
 *
 * Usado pelo AgendaWidget (U.3 / FEAT-V2-014) pra calcular ranges de
 * hoje/semana/mês.
 */

export type AgendaViewMode = 'today' | 'week' | 'month';

export interface DateRange {
  start: Date;
  end: Date;
}

/** Início do dia (00:00:00.000 local). */
export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Fim do dia (23:59:59.999 local). */
export function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/**
 * Início da semana — segunda 00:00 local (weekStartsOn=1, convenção BR).
 * Se hoje é segunda, retorna hoje 00:00.
 */
export function startOfWeek(d: Date): Date {
  const x = startOfDay(d);
  const day = x.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
  const diffToMonday = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diffToMonday);
  return x;
}

/** Fim da semana — domingo 23:59 local. */
export function endOfWeek(d: Date): Date {
  const start = startOfWeek(d);
  const x = new Date(start);
  x.setDate(x.getDate() + 6);
  return endOfDay(x);
}

/** Início do mês — dia 1 às 00:00 local. */
export function startOfMonth(d: Date): Date {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
}

/** Fim do mês — último dia às 23:59 local. */
export function endOfMonth(d: Date): Date {
  const x = startOfMonth(d);
  x.setMonth(x.getMonth() + 1);
  x.setDate(0); // Vai pro último dia do mês anterior (que é nosso mês)
  return endOfDay(x);
}

/**
 * Calcula o range completo (start/end) pra um viewMode. Usado pelo
 * useCalendarRange + AgendaWidget pra montar params da request.
 *
 * `reference` permite testar com data fixa; em produção passa `new Date()`.
 */
export function rangeForViewMode(viewMode: AgendaViewMode, reference: Date = new Date()): DateRange {
  switch (viewMode) {
    case 'today':
      return { start: startOfDay(reference), end: endOfDay(reference) };
    case 'week':
      return { start: startOfWeek(reference), end: endOfWeek(reference) };
    case 'month':
      // Mês ESTENDIDO até última semana completa pra calendar grid (até 6 weeks)
      return {
        start: startOfWeek(startOfMonth(reference)),
        end: endOfWeek(endOfMonth(reference)),
      };
  }
}

/**
 * Verifica se duas Date são o mesmo dia (mesmo ano/mês/dia).
 * Usado pelo calendar grid pra agrupar eventos por dia.
 */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Gera array de Dates representando todos os dias do range (inclusivo).
 * Usado pra renderizar grids (week=7 dias, month=até 42 dias).
 */
export function daysInRange(range: DateRange): Date[] {
  const days: Date[] = [];
  const cursor = startOfDay(range.start);
  const end = startOfDay(range.end);
  while (cursor <= end) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}
